import { forwardRef, useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronDown, Check } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface CountryOption {
  code: string;
  dialCode: string;
  flag: string;
  name: string;
  placeholder: string;
  maxDigits: number;
}

const COUNTRIES: CountryOption[] = [
  { code: 'US', dialCode: '+1',  flag: '🇺🇸', name: 'United States',      placeholder: '555-123-4567',    maxDigits: 10 },
  { code: 'CA', dialCode: '+1',  flag: '🇨🇦', name: 'Canada',             placeholder: '555-123-4567',    maxDigits: 10 },
  { code: 'PH', dialCode: '+63', flag: '🇵🇭', name: 'Philippines',        placeholder: '912 345 6789',    maxDigits: 10 },
  { code: 'MX', dialCode: '+52', flag: '🇲🇽', name: 'Mexico',             placeholder: '55 1234 5678',    maxDigits: 10 },
  { code: 'GB', dialCode: '+44', flag: '🇬🇧', name: 'United Kingdom',     placeholder: '7700 123456',     maxDigits: 10 },
  { code: 'AU', dialCode: '+61', flag: '🇦🇺', name: 'Australia',          placeholder: '412 345 678',     maxDigits:  9 },
  { code: 'OTHER', dialCode: '', flag: '🌐', name: 'Other / International', placeholder: '+XX XXX XXX XXXX', maxDigits: 20 },
];

function detectCountry(value: string): CountryOption {
  if (!value) return COUNTRIES[0];
  const v = value.trim();
  if (v.startsWith('+63')) return COUNTRIES.find(c => c.code === 'PH')!;
  if (v.startsWith('+52')) return COUNTRIES.find(c => c.code === 'MX')!;
  if (v.startsWith('+44')) return COUNTRIES.find(c => c.code === 'GB')!;
  if (v.startsWith('+61')) return COUNTRIES.find(c => c.code === 'AU')!;
  if (v.startsWith('+1'))  return COUNTRIES[0]; // US

  const digits = v.replace(/\D/g, '');
  if (digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))) {
    return COUNTRIES[0]; // Legacy US
  }

  if (v.startsWith('+')) return COUNTRIES.find(c => c.code === 'OTHER')!;
  return COUNTRIES[0];
}

function extractNationalDigits(value: string, country: CountryOption): string {
  if (!value) return '';
  const v = value.trim();
  if (country.code === 'OTHER') return v;

  if (country.dialCode && v.startsWith(country.dialCode)) {
    return v.slice(country.dialCode.length).replace(/\D/g, '');
  }

  const digits = v.replace(/\D/g, '');
  if ((country.code === 'US' || country.code === 'CA') && digits.length === 11 && digits.startsWith('1')) {
    return digits.slice(1);
  }
  return digits;
}

function formatNational(digits: string, country: CountryOption): string {
  if (country.code === 'OTHER') return digits;

  const d = digits.replace(/\D/g, '').slice(0, country.maxDigits);

  if (country.code === 'US' || country.code === 'CA') {
    // Issue #401: display/entry mask is 555-123-4567 (stored value is unchanged).
    if (d.length < 4) return d;
    if (d.length < 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6, 10)}`;
  }

  if (country.code === 'PH') {
    // 9XX XXX XXXX
    if (d.length < 4) return d;
    if (d.length < 7) return `${d.slice(0, 3)} ${d.slice(3)}`;
    return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 10)}`;
  }

  if (country.code === 'MX') {
    // XX XXXX XXXX
    if (d.length < 3) return d;
    if (d.length < 7) return `${d.slice(0, 2)} ${d.slice(2)}`;
    return `${d.slice(0, 2)} ${d.slice(2, 6)} ${d.slice(6, 10)}`;
  }

  if (country.code === 'GB') {
    // XXXX XXXXXX
    if (d.length < 5) return d;
    return `${d.slice(0, 4)} ${d.slice(4, 10)}`;
  }

  if (country.code === 'AU') {
    // XXX XXX XXX
    if (d.length < 4) return d;
    if (d.length < 7) return `${d.slice(0, 3)} ${d.slice(3)}`;
    return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 9)}`;
  }

  return d;
}

function buildStoredValue(display: string, country: CountryOption): string {
  if (country.code === 'OTHER') return display;
  const digits = display.replace(/\D/g, '').slice(0, country.maxDigits);
  if (!digits) return '';
  return `${country.dialCode}${digits}`;
}

export interface PhoneInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value?: string;
  onChange?: (value: string) => void;
}

const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value, onChange, className, ...props }, ref) => {
    const [country, setCountry] = useState<CountryOption>(() => detectCountry(value || ''));
    const [displayValue, setDisplayValue] = useState(() => {
      const nat = extractNationalDigits(value || '', detectCountry(value || ''));
      return formatNational(nat, detectCountry(value || ''));
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
      const detected = detectCountry(value || '');
      setCountry(detected);
      if (detected.code === 'OTHER') {
        setDisplayValue(value || '');
      } else {
        const nat = extractNationalDigits(value || '', detected);
        setDisplayValue(formatNational(nat, detected));
      }
    }, [value]);

    const handleCountryChange = (newCountry: CountryOption) => {
      setCountry(newCountry);
      setOpen(false);
      // Keep digits, reformat for new country
      const currentDigits = displayValue.replace(/\D/g, '');
      const newDisplay = newCountry.code === 'OTHER' ? displayValue : formatNational(currentDigits, newCountry);
      setDisplayValue(newDisplay);
      onChange?.(buildStoredValue(newDisplay, newCountry));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      if (country.code === 'OTHER') {
        setDisplayValue(input);
        onChange?.(input);
        return;
      }
      const digits = input.replace(/\D/g, '');
      if (digits.length <= country.maxDigits) {
        const formatted = formatNational(digits, country);
        setDisplayValue(formatted);
        onChange?.(buildStoredValue(formatted, country));
      }
    };

    return (
      <div className={cn("flex", className)}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-[90px] shrink-0 justify-between gap-1 rounded-r-none border-r-0 px-2 font-normal focus:z-10"
            >
              <span className="text-base leading-none">{country.flag}</span>
              <span className="text-xs text-muted-foreground">{country.dialCode || '🌐'}</span>
              <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[240px] p-1">
            {COUNTRIES.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => handleCountryChange(c)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground",
                  country.code === c.code && "bg-accent"
                )}
              >
                <span className="text-base w-6 text-center">{c.flag}</span>
                <span className="flex-1 text-left">{c.name}</span>
                <span className="text-xs text-muted-foreground">{c.dialCode}</span>
                {country.code === c.code && <Check className="h-3 w-3 ml-auto" />}
              </button>
            ))}
          </PopoverContent>
        </Popover>
        <Input
          {...props}
          ref={ref}
          type="tel"
          value={displayValue}
          onChange={handleChange}
          placeholder={country.placeholder}
          className="rounded-l-none"
        />
      </div>
    );
  }
);

PhoneInput.displayName = "PhoneInput";

export { PhoneInput };
